import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { canSeeAmounts } from '@/lib/roles';
import { fmtDate, fmtINR } from '@/lib/format';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { useBulkSelection } from '@/lib/useBulkSelection';
import { VisitReconciliationStrip } from '@/components/wo/VisitReconciliationStrip';
import { AccountsAlertPanel } from '@/components/wo/AccountsAlertPanel';
import {
  Plus, MapPin, CalendarClock, ReceiptText, AlertTriangle, Plane, Building2, Users as UsersIcon,
} from 'lucide-react';

const BULK_ROLES = ['owner', 'operations', 'accounts'];

const STATUS_LABEL = {
  planned: 'Planned', completed: 'Completed',
  cancelled_before_travel: 'Cancelled before travel',
  cancelled_on_arrival:    'Cancelled on arrival',
  rescheduled:             'Rescheduled',
  cancelled: 'Cancelled',        // legacy — remapped server-side
};
const STATUS_CHIP = {
  planned: 'chip-info', completed: 'chip-success',
  cancelled_before_travel: '',
  cancelled_on_arrival:    'chip-warning',
  rescheduled:             'chip-info',
  cancelled: '',
};
// Iter41 — statuses selectable at create / edit time (legacy `cancelled` is
// server-remapped and never surfaced in the dropdown).
const VISIT_STATUSES_SELECTABLE = [
  { key: 'planned',                  label: 'Planned' },
  { key: 'completed',                label: 'Completed' },
  { key: 'cancelled_before_travel',  label: 'Cancelled before travel' },
  { key: 'cancelled_on_arrival',     label: 'Cancelled on arrival' },
  { key: 'rescheduled',              label: 'Rescheduled' },
];
const BILL_LABEL = {
  not_billable: 'Not billable', pending_bill: 'Pending bill',
  billed: 'Billed', on_hold: 'On hold', waived: 'Waived',
};
const BILL_CHIP = {
  not_billable: '', pending_bill: 'chip-warning', billed: 'chip-success',
  on_hold: '', waived: '',
};

// Iter40 — canonical non-billable reasons (mirror of NON_BILLABLE_REASONS in visits_router.py)
const NON_BILLABLE_REASONS = [
  { key: 'refund',      label: 'Refund' },
  { key: 'goodwill',    label: 'Goodwill' },
  { key: 'promotional', label: 'Promotional' },
  { key: 'rework',      label: 'Rework' },
  { key: 'internal',    label: 'Internal' },
  { key: 'dispute',     label: 'Dispute' },
  { key: 'other',       label: 'Other' },
];
const NB_LABEL = Object.fromEntries(NON_BILLABLE_REASONS.map(r => [r.key, r.label]));

const CAN_SEE_TILES = ['owner', 'operations', 'accounts', 'commercials'];
const CAN_MODIFY_BILLING = ['owner', 'operations', 'accounts', 'commercials'];

export default function VisitsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tiles, setTiles] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [newVisit, setNewVisit] = useState(null);
  const [editBill, setEditBill] = useState(null);
  const [reasonFilter, setReasonFilter] = useState('');  // iter40 — Accounts filter
  const canSeeTiles = CAN_SEE_TILES.includes(user.role);
  const canModifyBilling = CAN_MODIFY_BILLING.includes(user.role);
  const canBulk = BULK_ROLES.includes(user.role);
  const sel = useBulkSelection(rows);

  const load = async () => {
    try {
      const params = reasonFilter ? { non_billable_reason: reasonFilter } : {};
      const [v, ps, us] = await Promise.all([
        api.get('/visits', { params }),
        api.get('/projects'),
        canSeeTiles ? api.get('/users') : Promise.resolve({ data: [] }),
      ]);
      setRows(v.data || []); setProjects(ps.data || []); setUsers(us.data || []);
      if (canSeeTiles) {
        const t = await api.get('/visits/tiles');
        setTiles(t.data);
      }
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, [reasonFilter]);
  useAutoRefetch(load, { collections: ['visits', 'master_projects', 'work_orders'] });

  const userMap = useMemo(() => Object.fromEntries((users || []).map(u => [u.id, u])), [users]);
  const projMap = useMemo(() => Object.fromEntries((projects || []).map(p => [p.id, p])), [projects]);

  return (
    <AppShell breadcrumbs={[{ label: 'Travel & Visits' }]}>
      <div className="p-6 max-w-[1400px] mx-auto fade-up" data-testid="visits-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Plane size={22} />Travel &amp; Visits</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Site visits with quota-aware billing intelligence.</p>
          </div>
          <Button size="sm" onClick={() => setNewVisit({ project_id: '', visit_date: '', purpose: '', location: '', duration_hours: '', status: 'planned', notes: '', expense_drive_link: '' })} data-testid="visits-new-cta"><Plus size={14} className="mr-1" />New visit</Button>
        </div>

        <AccountsAlertPanel className="mb-4" />

        {tiles && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="visits-tiles">
            <Tile icon={CalendarClock} label="This month" value={tiles.visits_this_month} hint="Completed visits" />
            <Tile icon={ReceiptText} label="Unbilled visits" value={tiles.unbilled_visits} hint={fmtINR(tiles.unbilled_value_inr)} />
            <Tile icon={Plane} label="Upcoming (7d)" value={tiles.upcoming_next_7_days} />
            <Tile icon={AlertTriangle} label="Over-quota projects" value={tiles.over_quota_projects.length} hint={tiles.over_quota_projects.map(p => `${p.project_name}: +${p.over_by}`).slice(0, 2).join(' · ') || '—'} />
          </div>
        )}

        {/* Iter42 — per-project reconciliation strips for the projects the
            caller can see with active visits (max 6 to keep the page lean). */}
        <ProjectReconciliationList rows={rows} />

        <div className="card overflow-x-auto">
          {canModifyBilling && (
            <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap"
                 style={{ borderColor: 'hsl(var(--border))' }}
                 data-testid="visits-filter-bar">
              <span className="text-[11.5px] text-[hsl(var(--foreground-muted))] uppercase tracking-wider">Non-billable filter</span>
              <button onClick={() => setReasonFilter('')}
                      className={`chip ${!reasonFilter ? 'chip-accent' : ''}`}
                      data-testid="visits-filter-all">All</button>
              {NON_BILLABLE_REASONS.map(r => (
                <button key={r.key}
                        onClick={() => setReasonFilter(r.key)}
                        className={`chip ${reasonFilter === r.key ? 'chip-accent' : ''}`}
                        data-testid={`visits-filter-${r.key}`}>{r.label}</button>
              ))}
            </div>
          )}
          {canBulk && (
            <div className="p-3 pb-0">
              <BulkActionBar
                module="visits" moduleLabel="Trips & Visits"
                selectedIds={sel.selectedIds} onClear={sel.clear} onApplied={load}
                needsApproval={user?.role !== 'owner'}
                editableFields={['status', 'billing_status', 'record_state']}
              />
            </div>
          )}
          <table className="tbl" data-testid="visits-table">
            <thead>
              <tr>
                {canBulk && (
                  <th style={{ width: 32 }}>
                    <input type="checkbox" checked={sel.allChecked}
                      ref={el => el && (el.indeterminate = sel.indeterminate)}
                      onChange={sel.toggleAll} aria-label="Select all"
                      data-testid="bulk-select-all-visits" />
                  </th>
                )}
                <th>Date</th><th>Project</th><th>Purpose / Location</th>
                <th>Visitors</th><th>Duration</th><th>Status</th>
                <th>Quota</th><th>Fee</th><th>Billing</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={canBulk ? 11 : 10} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No visits logged yet.</td></tr>}
              {rows.map(v => (
                <tr key={v.id} data-testid={`visit-row-${v.id}`}>
                  {canBulk && (
                    <td>
                      <input type="checkbox" checked={sel.isSelected(v.id)}
                        onChange={() => sel.toggle(v.id)}
                        aria-label={`Select visit ${v.id}`}
                        data-testid={`visit-select-${v.id}`} />
                    </td>
                  )}
                  <td className="text-[12.5px] whitespace-nowrap">{fmtDate(v.visit_date)}</td>
                  <td className="text-[12.5px]">{v.project_name}</td>
                  <td>
                    <div className="text-[13px]">{v.purpose}</div>
                    {v.location && <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] inline-flex items-center gap-1 mt-0.5"><MapPin size={10} />{v.location}</div>}
                  </td>
                  <td className="text-[12.5px]">{(v.visitor_user_ids || []).map(id => (userMap[id]?.name || '')).filter(Boolean).join(', ') || '—'}</td>
                  <td className="tabular-nums text-[12.5px]">{v.duration_hours ? `${v.duration_hours} h` : '—'}</td>
                  <td>
                    <span className={`chip ${STATUS_CHIP[v.status] || ''}`}>{STATUS_LABEL[v.status] || v.status}</span>
                    {v.status === 'cancelled_on_arrival' && v.cancel_on_arrival_reason && (
                      <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-0.5 max-w-[220px] truncate"
                           title={v.cancel_on_arrival_reason}
                           data-testid={`visit-coa-reason-${v.id}`}>
                        {v.cancel_on_arrival_reason}
                      </div>
                    )}
                    {v.status === 'rescheduled' && (v.reschedule_history || []).length > 0 && (
                      <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-0.5"
                           data-testid={`visit-reschedule-${v.id}`}>
                        rescheduled from {v.reschedule_history[v.reschedule_history.length - 1].from_date}
                      </div>
                    )}
                  </td>
                  <td className="text-[12px]">
                    {v.quota_index != null ? (
                      <span title={`Included quota: ${v.included_quota}`}>
                        {v.quota_index} / {v.included_quota || 0}
                        {v.included_quota && v.quota_index > v.included_quota && (
                          <span className="chip chip-warning ml-1">Over</span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {v.billable ? <MaskedAmount amount={v.fee_inr} masked={v.fee_masked} /> :
                      <span className="text-[hsl(var(--foreground-subtle))]">—</span>}
                  </td>
                  <td>
                    {v.billing_status ? <span className={`chip ${BILL_CHIP[v.billing_status] || ''}`}>{BILL_LABEL[v.billing_status]}</span> :
                      (v.billable ? <span className="chip chip-warning">Pending bill</span> : <span className="text-[hsl(var(--foreground-subtle))]">—</span>)}
                    {v.invoice_ref && <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-0.5 font-mono">{v.invoice_ref}</div>}
                    {v.non_billable_reason && (
                      <div className="mt-0.5" data-testid={`visit-nb-reason-${v.id}`}>
                        <span className="chip text-[10px]">{NB_LABEL[v.non_billable_reason] || v.non_billable_reason}</span>
                      </div>
                    )}
                  </td>
                  <td className="text-right">
                    {canModifyBilling && v.status === 'completed' && v.billable && (
                      <Button size="sm" variant="outline" data-testid={`visit-bill-${v.id}`}
                              onClick={() => setEditBill({ ...v, billing_status: v.billing_status || 'pending_bill', invoice_ref: v.invoice_ref || '', note: '' })}>Bill</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* NEW VISIT */}
        <Dialog open={!!newVisit} onOpenChange={o => !o && setNewVisit(null)}>
          <DialogContent data-testid="visits-new-dialog">
            <DialogHeader><DialogTitle>New visit</DialogTitle></DialogHeader>
            {newVisit && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Project" full>
                  <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                          value={newVisit.project_id} onChange={e => setNewVisit({ ...newVisit, project_id: e.target.value })} data-testid="visits-new-project">
                    <option value="">Select project…</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Visit date"><Input type="date" value={newVisit.visit_date} onChange={e => setNewVisit({ ...newVisit, visit_date: e.target.value })} data-testid="visits-new-date" /></Field>
                <Field label="Duration (hours)"><Input type="number" step="0.5" value={newVisit.duration_hours} onChange={e => setNewVisit({ ...newVisit, duration_hours: e.target.value })} /></Field>
                <Field label="Purpose" full><Input value={newVisit.purpose} onChange={e => setNewVisit({ ...newVisit, purpose: e.target.value })} data-testid="visits-new-purpose" /></Field>
                <Field label="Location" full><Input value={newVisit.location} onChange={e => setNewVisit({ ...newVisit, location: e.target.value })} /></Field>
                <Field label="Status">
                  <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                          value={newVisit.status} onChange={e => setNewVisit({ ...newVisit, status: e.target.value })}
                          data-testid="visits-new-status">
                    {VISIT_STATUSES_SELECTABLE.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </Field>
                {newVisit.status === 'cancelled_on_arrival' && (
                  <Field label="Reason (required) — visit still counts against included allowance" full>
                    <Textarea rows={2}
                              value={newVisit.cancel_on_arrival_reason || ''}
                              onChange={e => setNewVisit({ ...newVisit, cancel_on_arrival_reason: e.target.value })}
                              placeholder="Why was it cancelled after arriving on site?"
                              data-testid="visits-new-coa-reason" />
                  </Field>
                )}
                <Field label="Expense Drive link (optional)"><Input placeholder="https://drive.google.com/…" value={newVisit.expense_drive_link} onChange={e => setNewVisit({ ...newVisit, expense_drive_link: e.target.value })} /></Field>
                <Field label="Notes" full><Textarea rows={2} value={newVisit.notes} onChange={e => setNewVisit({ ...newVisit, notes: e.target.value })} /></Field>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewVisit(null)}>Cancel</Button>
              <Button onClick={async () => {
                if (newVisit.status === 'cancelled_on_arrival' && !(newVisit.cancel_on_arrival_reason || '').trim()) {
                  toast.error('A reason is required for Cancelled on Arrival.'); return;
                }
                try {
                  const body = { ...newVisit };
                  if (body.duration_hours === '') delete body.duration_hours; else body.duration_hours = Number(body.duration_hours);
                  Object.keys(body).forEach(k => { if (body[k] === '') delete body[k]; });
                  await api.post('/visits', body);
                  toast.success('Visit logged'); setNewVisit(null); load();
                } catch (e) { toast.error(parseErr(e)); }
              }} disabled={!newVisit?.project_id || !newVisit?.visit_date || !newVisit?.purpose?.trim()} data-testid="visits-new-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* BILLING DIALOG */}
        <Dialog open={!!editBill} onOpenChange={o => !o && setEditBill(null)}>
          <DialogContent data-testid="visits-bill-dialog">
            <DialogHeader><DialogTitle>Billing — {editBill?.project_name} · {fmtDate(editBill?.visit_date)}</DialogTitle></DialogHeader>
            {editBill && (
              <>
                <div className="text-[13px] text-[hsl(var(--foreground-muted))]">
                  {editBill.purpose} · Fee: <b>{fmtINR(editBill.fee_inr)}</b>
                </div>
                <Field label="Billing status">
                  <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                          value={editBill.billing_status} onChange={e => setEditBill({ ...editBill, billing_status: e.target.value })} data-testid="visits-bill-status">
                    {Object.entries(BILL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Field>
                {(editBill.billing_status === 'not_billable' || editBill.billing_status === 'waived') && (
                  <Field label="Why non-billable? (required)">
                    <select className="h-9 w-full border rounded-md px-2 text-[14px]"
                            style={{ borderColor: 'hsl(var(--border))' }}
                            value={editBill.non_billable_reason || ''}
                            onChange={e => setEditBill({ ...editBill, non_billable_reason: e.target.value })}
                            data-testid="visits-bill-reason">
                      <option value="">— Choose reason —</option>
                      {NON_BILLABLE_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="Invoice ref (optional)"><Input value={editBill.invoice_ref} onChange={e => setEditBill({ ...editBill, invoice_ref: e.target.value })} /></Field>
                <Field label="Note (optional)"><Textarea rows={2} value={editBill.note} onChange={e => setEditBill({ ...editBill, note: e.target.value })} /></Field>
              </>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditBill(null)}>Cancel</Button>
              <Button onClick={async () => {
                const nbRequired = editBill.billing_status === 'not_billable' || editBill.billing_status === 'waived';
                if (nbRequired && !editBill.non_billable_reason) {
                  toast.error('Please choose a non-billable reason.'); return;
                }
                try {
                  await api.post(`/visits/${editBill.id}/billing`, {
                    billing_status: editBill.billing_status,
                    invoice_ref: editBill.invoice_ref || null,
                    note: editBill.note || null,
                    non_billable_reason: editBill.non_billable_reason || null,
                  });
                  toast.success('Billing updated'); setEditBill(null); load();
                } catch (e) { toast.error(parseErr(e)); }
              }} data-testid="visits-bill-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

const Tile = ({ icon: Icon, label, value, hint }) => (
  <div className="card p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
        <div className="font-heading text-2xl mt-1 tabular-nums">{value}</div>
        {hint && <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5 truncate">{hint}</div>}
      </div>
      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground-muted))' }}><Icon size={16} /></div>
    </div>
  </div>
);

const Field = ({ label, full, children }) => (
  <label className={`block ${full ? 'col-span-2' : ''}`}>
    <span className="text-[12px] text-[hsl(var(--foreground-muted))]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

// Iter42 — per-project reconciliation grid rendered above the visits table.
// Pure read: uses the /visits/reconciliation/:pid endpoint (no writes).
function ProjectReconciliationList({ rows }) {
  const projectMap = new Map();
  (rows || []).forEach(r => {
    if (!r?.project_id) return;
    if (!projectMap.has(r.project_id)) {
      projectMap.set(r.project_id, { id: r.project_id, name: r.project_name || 'Untitled project' });
    }
  });
  const projects = Array.from(projectMap.values()).slice(0, 6);
  if (projects.length === 0) return null;
  return (
    <div className="mb-4" data-testid="visits-reconciliation-list">
      <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] uppercase tracking-wider mb-2">
        Reconciliation by project
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {projects.map(p => (
          <div key={p.id}>
            <div className="text-[12.5px] font-medium mb-1 truncate">{p.name}</div>
            <VisitReconciliationStrip projectId={p.id} compact />
          </div>
        ))}
      </div>
    </div>
  );
}
